import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Palette, DollarSign, Clock } from 'lucide-react';

const FluxVsMidjourney = () => {
  const comparisonData = [
    { feature: 'Image Quality', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐⭐' },
    { feature: 'Text Rendering', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐' },
    { feature: 'Prompt Understanding', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐' },
    { feature: 'Photorealism', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐' },
    { feature: 'Artistic Styles', flux: '⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐⭐' },
    { feature: 'Speed', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐' },
    { feature: 'Bulk Generation', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐' },
    { feature: 'API Access', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐' },
    { feature: 'Commercial Clarity', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐⭐' },
    { feature: 'Ease of Use', flux: '⭐⭐⭐⭐⭐', midjourney: '⭐⭐⭐' },
  ];

  const faqs = [
    {
      question: "Is FLUX better than Midjourney for commercial use?",
      answer: "FLUX offers clearer commercial licensing through official APIs, better text rendering for product images, and bulk generation capabilities that Midjourney lacks. For e-commerce and marketing, FLUX is often the better choice."
    },
    {
      question: "Which generates images faster, FLUX or Midjourney?",
      answer: "FLUX through our platform generates images in 3-5 seconds. Midjourney typically takes 30-60 seconds per image and requires Discord interaction, making FLUX significantly faster for production use."
    },
    {
      question: "Can FLUX match Midjourney's artistic quality?",
      answer: "FLUX 1.1 Pro matches or exceeds Midjourney in photorealism and text rendering. For purely artistic/stylized images, both are excellent. FLUX excels in commercial and product photography applications."
    },
    {
      question: "Why switch from Midjourney to FLUX?",
      answer: "Key reasons: API access for automation, bulk generation capabilities, better text rendering, clearer commercial licensing, no Discord requirement, and faster generation speeds."
    }
  ];

  const fluxAdvantages = [
    "Superior text rendering in images",
    "API access for automation & integration",
    "Bulk generation (100+ images/minute)",
    "No Discord required - web interface",
    "Clear commercial licensing",
    "Faster generation (3-5 seconds)"
  ];

  const midjourneyAdvantages = [
    "Strong artistic and stylized aesthetics",
    "Large community and prompt sharing",
    "Established reputation",
    "Good for concept art and illustration",
    "Regular style updates"
  ];

  return (
    <>
      <SEO
        title="FLUX vs Midjourney 2026 | Complete AI Image Generator Comparison"
        description="Comprehensive comparison of FLUX and Midjourney AI image generators. Quality, speed, pricing, bulk generation, and commercial use compared. Which is better for your needs?"
        keywords="flux vs midjourney, ai image generator comparison, flux 1.1 pro vs midjourney, midjourney alternative, best ai image generator 2026, flux ai review, midjourney review, flux or midjourney"
        url="/flux-vs-midjourney"
        type="article"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'FLUX vs Midjourney', url: '/flux-vs-midjourney' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-violet-500/10 text-violet-500 border-violet-500/20">
                2026 Comparison Guide
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                FLUX vs Midjourney
                <span className="block text-violet-500">Which AI Is Best for You?</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Comprehensive comparison of the two leading AI image generators. 
                Quality, speed, pricing, and real-world use cases analyzed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-violet-500 hover:bg-violet-600">
                  <Link to="/try-generator">
                    Try FLUX Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Feature-by-Feature Comparison
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card>
                <CardContent className="p-0">
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 font-semibold border-b">
                    <div>Feature</div>
                    <div className="text-center text-violet-500">FLUX</div>
                    <div className="text-center text-fuchsia-500">Midjourney</div>
                  </div>
                  {comparisonData.map((row, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 p-4 border-b last:border-b-0">
                      <div className="font-medium">{row.feature}</div>
                      <div className="text-center">{row.flux}</div>
                      <div className="text-center">{row.midjourney}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pros Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* FLUX Advantages */}
              <Card className="border-violet-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-violet-500">
                    <Zap className="w-5 h-5" />
                    FLUX Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {fluxAdvantages.map((advantage, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0" />
                      <span>{advantage}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Midjourney Advantages */}
              <Card className="border-fuchsia-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-fuchsia-500">
                    <Palette className="w-5 h-5" />
                    Midjourney Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {midjourneyAdvantages.map((advantage, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-fuchsia-500 flex-shrink-0" />
                      <span>{advantage}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Pricing Comparison
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <Card className="border-violet-500/20">
                <CardHeader className="text-center">
                  <Badge className="w-fit mx-auto mb-2 bg-violet-500/10 text-violet-500">FLUX (via Our Platform)</Badge>
                  <CardTitle className="text-2xl">From $0/month</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-center text-muted-foreground">
                  <p>✓ Free tier: 5 images/day</p>
                  <p>✓ Hobby: $7.99/mo - 100 images</p>
                  <p>✓ Creator: $19.99/mo - 500 images</p>
                  <p>✓ Pro: $49.99/mo - 2000 images</p>
                  <p>✓ Bulk generation included</p>
                </CardContent>
              </Card>

              <Card className="border-fuchsia-500/20">
                <CardHeader className="text-center">
                  <Badge className="w-fit mx-auto mb-2 bg-fuchsia-500/10 text-fuchsia-500">Midjourney</Badge>
                  <CardTitle className="text-2xl">From $10/month</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-center text-muted-foreground">
                  <p>✓ Basic: $10/mo - 200 images</p>
                  <p>✓ Standard: $30/mo - unlimited relaxed</p>
                  <p>✓ Pro: $60/mo - unlimited + stealth</p>
                  <p>✗ No bulk generation</p>
                  <p>✗ Requires Discord</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Use Case Recommendations */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Which Should You Choose?
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Choose FLUX If You Need...</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p>• E-commerce product photography</p>
                  <p>• Text or logos rendered in images</p>
                  <p>• Bulk generation (100+ images)</p>
                  <p>• API access for automation</p>
                  <p>• Fast turnaround (seconds, not minutes)</p>
                  <p>• Clear commercial licensing</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Choose Midjourney If You Need...</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p>• Highly stylized artistic images</p>
                  <p>• Concept art and illustration</p>
                  <p>• Community prompt inspiration</p>
                  <p>• Specific Midjourney aesthetic</p>
                  <p>• Don't need bulk generation</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Try FLUX Quality for Free
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                See the FLUX difference yourself. Generate your first images free, 
                no credit card or Discord required.
              </p>
              <Button asChild size="lg" className="bg-violet-500 hover:bg-violet-600">
                <Link to="/try-generator">
                  Try FLUX Free Now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default FluxVsMidjourney;
